import React from 'react';
import styled from 'styled-components';
import { fieldDisplayName, isFieldFilled, isRequiredField, optionLabel, toggleGroupItem } from '../fillLayout';
import RequiredMark from './RequiredMark';

type Props = {
  item: any;
  keyName: string;
  tab: string;
  data: Record<string, any>;
  setData: (d: Record<string, any>) => void;
  onBlur: Function;
  // Inspection Notes wiring: focus registry, highlight-on-focus, Enter-to-advance.
  registerRef?: (key: string, el: HTMLElement | null) => void;
  onFocusField?: (key: string) => void;
  onAdvance?: (key: string) => void;
  // Read-only: the Results row is set from the inspection's Activity Status
  // (fillLayout.applyStatusToResult), never ticked here.
  locked?: boolean;
};

const CheckboxGroup = ({
  item,
  keyName,
  tab,
  data,
  setData,
  onBlur,
  registerRef = undefined,
  onFocusField = undefined,
  onAdvance = undefined,
  locked = false,
}: Props) => {
  const isRadio = item?.inputType === 'radio';
  const isYesNo = isRadio && item?.items?.length === 2;

  // toggleGroupItem identifies the option by INDEX, never by `item.name` — the
  // builder writes a literal 'BOX-NAME' into every option, so name-matching made
  // one click hit them all. Shared with the on-page canvas so the two cannot
  // drift apart.
  const onChange = (index, checked) => {
    setData(toggleGroupItem(data, tab, keyName, index, checked));
  };

  const handleBlur = () => {
    onBlur(data);
  };

  // Type the first letter of an option to pick it — 'y'/'n' on a Yes/No row is
  // the common case, but this works for any captions.
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      if (onAdvance) {
        e.preventDefault();
        onAdvance(keyName);
      }
      return;
    }
    if (e.key.length !== 1) return;

    const typed = e.key.toLowerCase();
    const match = item.items.findIndex(
      (box, i) => optionLabel(box.caption, i).charAt(0).toLowerCase() === typed,
    );
    if (match > -1) {
      e.preventDefault();
      onChange(match, true);
    }
  };

  return (
    <GroupWrapper
      $compact={isYesNo}
      // Required and nothing ticked: amber, like the on-page form.
      $missing={!locked && isRequiredField(keyName, item) && !isFieldFilled(item)}
    >
      <Question>
        {fieldDisplayName(keyName, item)}
        {isRequiredField(keyName, item) && <RequiredMark />}
        {locked && <LockedNote>Set from Activity Status</LockedNote>}
      </Question>
      <Options>
        {item.items.map((singleBox, index) => {
          const isChecked = singleBox.text === 'x';
          return (
            <OptionLabel key={index} $checked={isChecked} $locked={locked}>
              <input
                ref={el => registerRef && index === 0 && registerRef(keyName, el)}
                type={isRadio ? 'radio' : 'checkbox'}
                // Radios must share one name per question so the browser treats
                // them as a single group; checkboxes get a unique one. Scoped by
                // page + field key so two questions never collide.
                name={isRadio ? `${tab}-${keyName}` : `${tab}-${keyName}-${index}`}
                onChange={e => onChange(index, e.target.checked)}
                onFocus={() => onFocusField && onFocusField(keyName)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                checked={isChecked}
                disabled={locked}
              />
              <span>{optionLabel(singleBox.caption, index)}</span>
            </OptionLabel>
          );
        })}
      </Options>
    </GroupWrapper>
  );
};

export default CheckboxGroup;

const GroupWrapper = styled.div<{ $compact: boolean; $missing: boolean }>`
  grid-column: ${({ $compact }) => ($compact ? 'auto' : '1 / -1')};
  display: flex;
  flex-direction: ${({ $compact }) => ($compact ? 'row' : 'column')};
  align-items: ${({ $compact }) => ($compact ? 'center' : 'stretch')};
  justify-content: ${({ $compact }) => ($compact ? 'space-between' : 'flex-start')};
  gap: ${({ $compact }) => ($compact ? '12px' : '6px')};
  padding: ${({ $compact }) => ($compact ? '6px 12px' : '8px 12px 10px')};
  background: ${({ $missing }) => ($missing ? 'var(--warning-50)' : 'var(--neutral-50)')};
  border: 1px solid ${({ $missing }) => ($missing ? 'var(--warning-500)' : 'var(--border)')};
  border-radius: 10px;
  min-height: 40px;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;

  @media only screen and (max-width: 540px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const Question = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--mainText);
  line-height: 1.3;
  min-width: 0;
  flex: 1 1 auto;
`;

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 0 0 auto;
`;

const LockedNote = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--neutral-500);
`;

const OptionLabel = styled.label<{ $checked: boolean; $locked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--border)')};
  border-radius: 999px;
  background: ${({ $checked }) => ($checked ? 'var(--primary-50)' : 'var(--neutral-0)')};
  color: ${({ $checked }) => ($checked ? 'var(--primary-700)' : 'var(--neutral-700)')};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
  user-select: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ $checked }) => ($checked ? 'var(--primary-700)' : 'var(--neutral-400)')};
    background: ${({ $checked }) => ($checked ? 'var(--primary-50)' : 'var(--neutral-50)')};
  }

  &:focus-within {
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }

  input {
    margin: 0;
    width: 14px;
    height: 14px;
    accent-color: var(--primary-600);
    cursor: inherit;
  }
`;
